/**
 * View Milk Production - Display detailed milk production record (read-only)
 * Shows complete information about a recorded milk production entry
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Milk, AlertCircle, Edit } from 'lucide-react';
import { milkProductionApi, type MilkRecord } from '../../../services/gaushala/api';

export default function ViewMilkProduction() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [record, setRecord] = useState<MilkRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadRecord();
  }, [id]);

  const loadRecord = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!id) {
        setError('No record ID provided');
        setLoading(false);
        return;
      }

      console.log('🥛 Loading milk record ID:', id);
      const response = await milkProductionApi.getMilkRecordById(parseInt(id));
      console.log('🥛 API Response:', response);
      console.log('🥛 Response success:', response.success);
      console.log('🥛 Response data:', response.data);

      if (response.success && response.data) {
        console.log('✅ Record loaded successfully:', response.data);
        setRecord(response.data);
      } else {
        console.error('❌ Failed to load record:', response.error);
        setError(response.error || 'Failed to load record');
      }
    } catch (err) {
      console.error('❌ Error loading record:', err);
      setError('Error loading record details: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeColor = (status?: string): string => {
    switch (status?.toUpperCase()) {
      case 'RECORDED':
        return 'bg-blue-100 text-blue-800';
      case 'VERIFIED':
        return 'bg-green-100 text-green-800';
      case 'PROCESSED':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <button
          onClick={() => navigate('/gaushala/production')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft size={20} />
          Back to Records
        </button>

        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-4">
          <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-red-900">Error</h3>
            <p className="text-sm text-red-700 mt-1">{error || 'Record not found'}</p>
            <button
              onClick={() => navigate('/gaushala/production')}
              className="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Go back to records
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/gaushala/production')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} />
          Back to Records
        </button>
        <button
          onClick={() => navigate(`/gaushala/production/edit/${record.id}`)}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
        >
          <Edit size={20} />
          Edit Record
        </button>
      </div>

      {/* Title */}
      <div className="flex items-center gap-3 mb-6">
        <div className="rounded-full bg-blue-100 p-3">
          <Milk className="text-blue-600" size={24} />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Milk Production Record</h1>
          <p className="text-gray-600 text-sm">Details for {record.shedName || record.shedNumber}</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Card 1: Basic Information */}
        <div className="bg-white rounded-lg shadow p-6 md:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded p-4">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                Record ID
              </p>
              <p className="text-lg font-semibold text-gray-900">{record.id}</p>
            </div>

            <div className="bg-gray-50 rounded p-4">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                Shed Name
              </p>
              <p className="text-lg font-semibold text-gray-900">{record.shedName || record.shedNumber || '-'}</p>
            </div>

            <div className="bg-gray-50 rounded p-4">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                Gaushala ID
              </p>
              <p className="text-lg font-semibold text-gray-900">{record.gaushalaId || '-'}</p>
            </div>

            <div className="bg-gray-50 rounded p-4">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                Status
              </p>
              <span
                className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(
                  record.status
                )}`}
              >
                {record.status || '-'}
              </span>
            </div>
          </div>
        </div>

        {/* Card 2: Production Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Production Details</h2>
          <div className="space-y-4">
            <div className="border-b pb-4 last:border-b-0">
              <p className="text-sm text-gray-600 mb-1">Total Milk Quantity</p>
              <p className="text-2xl font-bold text-blue-600">
                {record.milkQuantity ? record.milkQuantity.toFixed(1) : '-'} <span className="text-sm text-gray-600">Liters</span>
              </p>
            </div>

            <div className="border-b pb-4 last:border-b-0">
              <p className="text-sm text-gray-600 mb-1">Fat Percentage</p>
              <p className="text-2xl font-bold text-orange-600">
                {record.fatPercentage ? record.fatPercentage.toFixed(1) : '-'} <span className="text-sm text-gray-600">%</span>
              </p>
            </div>

            <div className="border-b pb-4 last:border-b-0">
              <p className="text-sm text-gray-600 mb-1">SNF (Solids Not Fat)</p>
              <p className="text-2xl font-bold text-green-600">
                {record.snf ? record.snf.toFixed(1) : '-'} <span className="text-sm text-gray-600">%</span>
              </p>
            </div>
          </div>
        </div>

        {/* Card 3: Additional Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h2>
          <div className="space-y-4">
            <div className="border-b pb-4 last:border-b-0">
              <p className="text-sm text-gray-600 mb-2">Notes</p>
              <p className="text-gray-900 whitespace-pre-wrap">
                {record.notes || <span className="text-gray-400">No notes provided</span>}
              </p>
            </div>
          </div>
        </div>

        {/* Card 4: Audit Information */}
        <div className="bg-white rounded-lg shadow p-6 md:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Audit Information</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded p-4">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                Created Date
              </p>
              <p className="text-sm text-gray-900">{formatDate(record.createdAt)}</p>
            </div>

            <div className="bg-gray-50 rounded p-4">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                Created By
              </p>
              <p className="text-sm text-gray-900">{record.createdBy || '-'}</p>
            </div>

            <div className="bg-gray-50 rounded p-4">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                Updated Date
              </p>
              <p className="text-sm text-gray-900">{formatDate(record.updatedAt)}</p>
            </div>

            <div className="bg-gray-50 rounded p-4">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                Updated By
              </p>
              <p className="text-sm text-gray-900">{record.updatedBy || '-'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-8 flex gap-4 justify-center">
        <button
          onClick={() => navigate('/gaushala/production')}
          className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-gray-700"
        >
          Close
        </button>
        <button
          onClick={() => navigate(`/gaushala/production/edit/${record.id}`)}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2"
        >
          <Edit size={18} />
          Edit Record
        </button>
      </div>
    </div>
  );
}
